/**
 * 접근성(A11y) 유틸리티 함수들
 * 
 * 웹 접근성을 향상시키기 위한 헬퍼 함수들과 ARIA 속성 생성기를 제공합니다.
 * WCAG 2.1 가이드라인을 준수하며, 스크린 리더와 키보드 네비게이션을 지원합니다.
 */

// ARIA 레이블 생성기
export const createAriaLabel = {
  /**
   * 테이블 행에 대한 ARIA 레이블 생성
   */
  tableRow: (rowData: Record<string, any>, primaryKey: string) => {
    const primaryValue = rowData[primaryKey];
    return `테이블 행: ${primaryValue}`;
  },

  /**
   * 버튼 액션에 대한 ARIA 레이블 생성
   */
  button: (action: string, target?: string) => {
    return target ? `${target} ${action}` : action;
  },

  /**
   * 상태 표시기에 대한 ARIA 레이블 생성
   */
  status: (status: string, description?: string) => {
    return description ? `상태: ${status}, ${description}` : `상태: ${status}`;
  },

  /**
   * 폼 필드 에러에 대한 ARIA 레이블 생성
   */
  fieldError: (fieldName: string, errorMessage: string) => {
    return `${fieldName} 오류: ${errorMessage}`;
  },

  /**
   * 네비게이션 아이템에 대한 ARIA 레이블 생성
   */
  navigation: (itemName: string, isCurrent: boolean = false) => {
    return isCurrent ? `현재 페이지: ${itemName}` : `${itemName} 페이지로 이동`;
  },

  /**
   * 프로그래스바에 대한 ARIA 레이블 생성
   */
  progress: (value: number, max: number = 100, description?: string) => {
    const percentage = Math.round((value / max) * 100);
    return description ? `${description}: ${percentage}% 완료` : `진행률 ${percentage}%`;
  },
};

// 키보드 네비게이션 헬퍼
export const keyboardNavigation = {
  /**
   * 키보드 이벤트 핸들러 생성
   */
  createKeyHandler: (handlers: Record<string, () => void>) => {
    return (event: React.KeyboardEvent) => {
      const handler = handlers[event.key];
      if (handler) {
        event.preventDefault();
        handler();
      }
    };
  },

  /**
   * 엔터/스페이스 키로 클릭 이벤트 트리거
   */
  createActivationHandler: (onClick: () => void) => {
    return (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    };
  },

  /**
   * 방향키로 포커스 이동
   */
  createArrowNavigation: (
    items: Element[],
    getCurrentIndex: () => number,
    onIndexChange: (index: number) => void
  ) => {
    return (event: React.KeyboardEvent) => {
      const currentIndex = getCurrentIndex();
      let newIndex = currentIndex;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          newIndex = (currentIndex + 1) % items.length;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = items.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      onIndexChange(newIndex);
      (items[newIndex] as HTMLElement)?.focus();
    };
  },
};

// 색상 대비 유틸리티
export const colorContrast = {
  /**
   * 색상의 상대적 밝기 계산 (WCAG 공식)
   */
  getLuminance: (hex: string): number => {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0;

    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  },

  /**
   * 두 색상 간의 대비율 계산
   */
  getContrastRatio: (color1: string, color2: string): number => {
    const l1 = colorContrast.getLuminance(color1);
    const l2 = colorContrast.getLuminance(color2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    
    return (lighter + 0.05) / (darker + 0.05);
  },

  /**
   * WCAG AA 기준 (4.5:1) 대비율 검사
   */
  meetsWCAGAA: (color1: string, color2: string): boolean => {
    return colorContrast.getContrastRatio(color1, color2) >= 4.5;
  },

  /**
   * WCAG AAA 기준 (7:1) 대비율 검사
   */
  meetsWCAGAAA: (color1: string, color2: string): boolean => {
    return colorContrast.getContrastRatio(color1, color2) >= 7;
  },
};

// 포커스 관리
export const focusManagement = {
  /**
   * 포커스 트랩 설정 (모달 등에서 사용)
   */
  createFocusTrap: (container: HTMLElement) => {
    const focusableElements = container.querySelectorAll(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
    ) as NodeListOf<HTMLElement>;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const trapFocus = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    container.addEventListener('keydown', trapFocus);

    // 정리 함수 반환
    return () => {
      container.removeEventListener('keydown', trapFocus);
    };
  },

  /**
   * 포커스 가능한 요소 찾기
   */
  getFocusableElements: (container: HTMLElement): HTMLElement[] => {
    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    return Array.from(container.querySelectorAll(selectors));
  },

  /**
   * 요소로 안전하게 포커스 이동
   */
  safeFocus: (element: HTMLElement | null, options?: FocusOptions) => {
    if (element && typeof element.focus === 'function') {
      element.focus(options);
    }
  },
};

// 스크린 리더 지원
export const screenReader = {
  /**
   * 스크린 리더 전용 텍스트 스타일
   */
  srOnlyStyle: {
    position: 'absolute' as const,
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap' as const,
    border: 0,
  },

  /**
   * 동적 컨텐츠 변경 알림
   */
  announceToScreenReader: (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.style.cssText = Object.entries(screenReader.srOnlyStyle)
      .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
      .join('; ');
    
    document.body.appendChild(announcement);
    announcement.textContent = message;

    // 메시지를 읽은 후 요소 제거
    setTimeout(() => {
      if (announcement.parentNode) {
        announcement.parentNode.removeChild(announcement);
      }
    }, 1000);
  },

  /**
   * 로딩 상태 알림
   */
  announceLoading: (message: string = '데이터를 불러오는 중입니다') => {
    screenReader.announceToScreenReader(message, 'polite');
  },

  /**
   * 에러 상태 알림
   */
  announceError: (message: string) => {
    screenReader.announceToScreenReader(`오류: ${message}`, 'assertive');
  },

  /**
   * 성공 상태 알림
   */
  announceSuccess: (message: string) => {
    screenReader.announceToScreenReader(`성공: ${message}`, 'polite');
  },
};

// 헬퍼 함수들
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

// React Hook으로 접근성 기능 사용
export const useAccessibility = () => {
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    screenReader.announceToScreenReader(message, priority);
  };

  const createKeyHandler = keyboardNavigation.createKeyHandler;
  const createActivationHandler = keyboardNavigation.createActivationHandler;

  return {
    announce,
    createKeyHandler,
    createActivationHandler,
    createAriaLabel,
    focusManagement,
    screenReader,
  };
};